import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface SequenceSummary {
  sequenceId: string;
  name: string;
  description: string | null;
  trigger: string;
  status: string;
  stepCount: number;
  enrolledCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  lastEditedByEmail?: string | null;
  lastEditedByRole?: string | null;
  lastEditedAt?: string | null;
}

export interface SequenceStats {
  emailsSent30d: number;
  avgOpenRate: number | null;
  avgClickRate: number | null;
  activeSequenceCount: number;
  enrolledContactCount: number;
  /** Enrollments parked because this company's send gate is shut. */
  heldContactCount: number;
  /** Whether real sending is switched on for this company. */
  sendingEnabled: boolean;
}

/**
 * A template as the backend registry describes it. Both the step picker and
 * the gallery read this, so neither keeps its own hand-maintained list.
 */
export interface TemplateCatalogEntry {
  templateId: string;
  label: string;
  subject: string;
  /** True when every merge field this template needs resolves from real data. */
  sendable: boolean;
  mergeFields: string[];
  /** Fields with no real source. Empty when sendable. */
  previewOnlyFields: string[];
}

export interface SequenceStepInput {
  order: number;
  dayOffset: number;
  subject: string;
  templateId: string;
  active: boolean;
}

export interface TemplatePreviewResult {
  templateId: string;
  subject: string;
  html: string;
  mergeFields: Record<string, string>;
  availableMergeFields: string[];
  sendable: boolean;
  previewOnlyFields: string[];
}

interface State {
  sequences: SequenceSummary[];
  stats: SequenceStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  updateSequenceStatus: (
    sequenceId: string,
    status: 'active' | 'draft' | 'paused'
  ) => Promise<void>;
  createSequence: (args: {
    name: string;
    description?: string;
    trigger: string;
    steps: SequenceStepInput[];
  }) => Promise<{ sequenceId: string }>;
  enrollLead: (sequenceId: string, leadId: string) => Promise<{ enrollmentId: string; deduped: boolean }>;
  unenrolLead: (enrollmentId: string, reason?: string) => Promise<void>;
  renderTemplatePreview: (args: {
    templateId: string;
    mergeFields?: Record<string, string>;
  }) => Promise<TemplatePreviewResult>;
  /** The template registry, loaded once per company. */
  templates: TemplateCatalogEntry[];
  templatesLoading: boolean;
}

export function useEmailSequences(companyId: string | undefined): State {
  const [sequences, setSequences] = useState<SequenceSummary[]>([]);
  const [stats, setStats] = useState<SequenceStats | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [templates, setTemplates] = useState<TemplateCatalogEntry[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(Boolean(companyId));

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // The template registry is server-side state that changes only on deploy, so
  // it is fetched once per company rather than on every refresh. Both the step
  // picker and the gallery read it, which is what stops them drifting from
  // what the backend can actually render.
  useEffect(() => {
    if (!companyId) {
      setTemplatesLoading(false);
      return;
    }
    let cancelled = false;
    const fn = httpsCallable<
      { action: 'listTemplates'; companyId: string },
      { templates: TemplateCatalogEntry[] }
    >(functions, 'emailSequenceAction');
    setTemplatesLoading(true);
    fn({ action: 'listTemplates', companyId })
      .then((resp) => {
        if (!cancelled) setTemplates(resp.data.templates);
      })
      .catch((err) => {
        // Surface rather than fail silently: an empty picker with no
        // explanation reads as "there are no templates".
        console.error('[useEmailSequences] listTemplates failed', err);
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const listFn = httpsCallable<
      { action: 'listSequences'; companyId: string },
      { sequences: SequenceSummary[] }
    >(functions, 'emailSequenceAction');
    const statsFn = httpsCallable<
      { action: 'getSequenceStats'; companyId: string },
      SequenceStats
    >(functions, 'emailSequenceAction');
    setLoading(true);
    setError(null);
    Promise.all([
      listFn({ action: 'listSequences', companyId }),
      statsFn({ action: 'getSequenceStats', companyId }),
    ])
      .then(([listResp, statsResp]) => {
        setSequences(listResp.data.sequences);
        setStats(statsResp.data);
      })
      .catch((err) => {
        console.error('[useEmailSequences] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, refreshKey]);

  const updateSequenceStatus = useCallback(
    async (sequenceId: string, status: 'active' | 'draft' | 'paused') => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'updateSequenceStatus';
          companyId: string;
          sequenceId: string;
          status: 'active' | 'draft' | 'paused';
        },
        { success: boolean }
      >(functions, 'emailSequenceAction');
      await fn({
        action: 'updateSequenceStatus',
        companyId,
        sequenceId,
        status,
      });
      refresh();
    },
    [companyId, refresh]
  );

  const createSequence = useCallback(
    async (args: {
      name: string;
      description?: string;
      trigger: string;
      steps: SequenceStepInput[];
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'createSequence';
          companyId: string;
          name: string;
          description?: string;
          trigger: string;
          steps: SequenceStepInput[];
        },
        { success: boolean; sequenceId: string }
      >(functions, 'emailSequenceAction');
      const result = await fn({ action: 'createSequence', companyId, ...args });
      refresh();
      return { sequenceId: result.data.sequenceId };
    },
    [companyId, refresh]
  );

  const enrollLead = useCallback(
    async (sequenceId: string, leadId: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'enrollLead';
          companyId: string;
          sequenceId: string;
          leadId: string;
        },
        { success: boolean; enrollmentId: string; deduped: boolean }
      >(functions, 'emailSequenceAction');
      const result = await fn({
        action: 'enrollLead',
        companyId,
        sequenceId,
        leadId,
      });
      refresh();
      return {
        enrollmentId: result.data.enrollmentId,
        deduped: result.data.deduped,
      };
    },
    [companyId, refresh]
  );

  const unenrolLead = useCallback(
    async (enrollmentId: string, reason?: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'unenrolLead';
          companyId: string;
          enrollmentId: string;
          reason?: string;
        },
        { success: boolean; alreadyStopped: boolean }
      >(functions, 'emailSequenceAction');
      await fn({ action: 'unenrolLead', companyId, enrollmentId, reason });
      refresh();
    },
    [companyId, refresh]
  );

  const renderTemplatePreview = useCallback(
    async (args: { templateId: string; mergeFields?: Record<string, string> }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'renderTemplatePreview';
          companyId: string;
          templateId: string;
          mergeFields?: Record<string, string>;
        },
        TemplatePreviewResult
      >(functions, 'emailSequenceAction');
      const result = await fn({
        action: 'renderTemplatePreview',
        companyId,
        templateId: args.templateId,
        mergeFields: args.mergeFields,
      });
      return result.data;
    },
    [companyId]
  );

  return {
    sequences,
    stats,
    loading,
    error,
    refresh,
    updateSequenceStatus,
    createSequence,
    enrollLead,
    unenrolLead,
    renderTemplatePreview,
    templates,
    templatesLoading,
  };
}
